import { Bar, Button, Dialog, DialogHeader, Icon, Menu, Position, Size, Tooltip } from '@busybusy/webapp-react-ui';
import { ArrowBackIcon, HiddenIcon, MinimizeNavIcon, SearchMagnifyingGlass } from 'assets/icons';
import { IconButton } from 'components';
import CompanyNavigationMenu from 'components/domain/account/CompanyNavigationMenu/CompanyNavigationMenu';
import ActiveMemberAvatar from 'components/domain/member/ActiveMemberAvatar/ActiveMemberAvatar';
import { LazyImportWrapper } from 'components/foundation/LazyImportWrapper/LazyImportWrapper';
import ReferralButton from 'components/foundation/buttons/ReferralButton/ReferralButton';
import ReferralConfirmationDialog from 'components/foundation/dialogs/ReferralConfirmationDialog/ReferralConfirmationDialog';
import ReferralDialog from 'components/foundation/dialogs/ReferralDialog/ReferralDialog';
import { useCommandPalletState } from 'contexts/CommandPalletContext/CommandPalletContext';
import { useOpenable } from 'hooks';
import useCompanyNavMenuAnalytics from 'hooks/analytics/useCompanyNavMenuAnalytics';
import useQuickSearchGraylog from 'hooks/analytics/useQuickSearchGraylog';
import useIsInIframe from 'hooks/store/useIsInIframe';
import { lazy } from 'react';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import useNavBarSettings from '../hooks/useNavBarSettings';
import './NavHeader.scss';

const AccountPreferencesDialog = lazy(
  () => import('components/domain/account/AccountPreferencesDialog/AccountPreferencesDialog')
);
const AccountSettings = lazy(() => import('containers/account-settings/AccountSettings/AccountSettings'));

export interface INavHeaderProps {
  toggleMenu: () => void;
}

const NavHeader = ({ toggleMenu }: INavHeaderProps) => {
  const myAccountOpenable = useOpenable();
  const preferencesOpenable = useOpenable();
  const referralOpenable = useOpenable();
  const referralConfirmationOpenable = useOpenable();
  const { open: openCommandPallet } = useCommandPalletState();
  const isCommandPalletEnabled = useFeatureFlags('COMMAND_PALLET');
  const quickSearchUserEvents = useQuickSearchGraylog();
  const { areAllNavItemsVisible } = useNavBarSettings();
  const isInIframe = useIsInIframe();
  const userEvents = useCompanyNavMenuAnalytics();
  const advocateAppReferral = useFeatureFlags('ADVOCATE_APP_REFERRAL');

  return (
    <Bar>
      <Menu
        className="side-nav-menu"
        position={Position.BOTTOM_START}
        trigger={(ref: any) => (
          <div ref={ref}>
            <Tooltip label={t('Profile')}>
              <ActiveMemberAvatar className={'side-nav-menu-button'} size={Size.SMALL} numInitials={2} />
            </Tooltip>
          </div>
        )}
        content={(closeMenu) => (
          <CompanyNavigationMenu
            closeMenu={closeMenu}
            onMyAccountClick={() => {
              userEvents.recordMyAccountClick();
              myAccountOpenable.open();
            }}
            onPreferencesClick={() => {
              userEvents.recordPreferencesClick();
              preferencesOpenable.open();
            }}
          />
        )}
        width="300px"
      />
      <div className={'mr-2'}>
        {areAllNavItemsVisible() === false && (
          <Tooltip
            label={t(
              'Some menu items have been hidden. To customize which menu items are visible, visit the Preferences section.'
            )}
          >
            <Icon svg={HiddenIcon} />
          </Tooltip>
        )}
        {!isInIframe && (
          <Tooltip label={t('Collapse Menu')}>
            <Button
              type="icon"
              size={Size.SMALL}
              onClick={() => {
                toggleMenu();
              }}
            >
              <Icon svg={MinimizeNavIcon} />
            </Button>
          </Tooltip>
        )}

        {isCommandPalletEnabled && (
          <Tooltip label={t('Quick Search')}>
            <Button
              type="icon"
              size={Size.SMALL}
              onClick={() => {
                quickSearchUserEvents.searchOpened(false);
                openCommandPallet();
              }}
            >
              <Icon svg={SearchMagnifyingGlass} />
            </Button>
          </Tooltip>
        )}

        {advocateAppReferral && (
          <Tooltip label={t('Referral Program')}>
            <div className={'referral-button'}>
              <ReferralButton onClick={referralOpenable.open} />
            </div>
          </Tooltip>
        )}
      </div>

      <Dialog isOpen={myAccountOpenable.isOpen} onClose={myAccountOpenable.close} size="full">
        <DialogHeader divider={true}>
          <IconButton svg={ArrowBackIcon} onClick={myAccountOpenable.close} />
          <h2>{t('Account Settings')}</h2>
        </DialogHeader>
        <LazyImportWrapper triggerLoad={myAccountOpenable.isOpen} catchErrors={true}>
          <AccountSettings className="my-9 mx-12" />
        </LazyImportWrapper>
      </Dialog>

      <LazyImportWrapper triggerLoad={preferencesOpenable.isOpen} catchErrors={true}>
        <AccountPreferencesDialog isOpen={preferencesOpenable.isOpen} onClose={preferencesOpenable.close} size="full" />
      </LazyImportWrapper>

      {advocateAppReferral && (
        <>
          <ReferralDialog
            isOpen={referralOpenable.isOpen}
            onClose={referralOpenable.close}
            onSubmitted={() => {
              referralOpenable.close();
              referralConfirmationOpenable.open();
            }}
          />
          <ReferralConfirmationDialog
            isOpen={referralConfirmationOpenable.isOpen}
            onClose={referralConfirmationOpenable.close}
          />
        </>
      )}
    </Bar>
  );
};

export default NavHeader;
